"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect } from "react";

import { isEeaOrUkRegion } from "@/lib/region";

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Disable PostHog when running locally (next dev).
    if (process.env.NODE_ENV === "development") {
      return;
    }
    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
    if (!key) {
      console.warn("NEXT_PUBLIC_POSTHOG_KEY is not set");
      return;
    }
    if (posthog.__loaded) return;

    posthog.init(key, {
      // Ingest through our own /ingest reverse proxy (see next.config.ts).
      api_host: "/ingest",
      // Where the toolbar / "view in PostHog" links point.
      ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://eu.i.posthog.com",
      defaults: "2025-05-24",
      // App Router pageviews are captured manually (see PostHogPageView).
      capture_pageview: false,
      capture_pageleave: true,
      // Anonymous events are still geo-enriched; this only controls profile cost.
      person_profiles: "identified_only",
      // Honor the browser's "Do Not Track" everywhere — the privacy policy says we do.
      respect_dnt: true,
      // EEA/UK visitors: store nothing on the device (no cookie, no localStorage).
      // Identity is a privacy-preserving server-side hash, so ePrivacy consent isn't
      // triggered and no cookie banner is needed. Requires "cookieless mode" enabled
      // in the PostHog project settings, or these events are dropped by ingestion.
      ...(isEeaOrUkRegion() ? { cookieless_mode: "always" as const } : {}),
    });

    // Super properties, matching what RN registers at client construction
    // (`lib/analytics/posthog.ts` there: environment, platform, is_app_clip).
    // Both clients report into one project, so `platform` is what separates a
    // web funnel from a mobile one — without it every shared event name
    // (`photo_captured`, `guest_session_started`, …) is an unsplittable total.
    posthog.register({
      platform: "web",
      environment: process.env.NODE_ENV,
    });
  }, []);

  return <PHProvider client={posthog}>{children}</PHProvider>;
}
